import { Avatar } from '@/kit/Avatar';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import { ExportFormatCode, type UserGridQuery, type UserGridRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { buttonLinkProps } from '../buttonLink';
import { UsersHeader, type UserFilter } from './UsersHeader';
import './UsersList.scss';

const pageSizeOptions = [10, 25, 50, 100];

interface RowActionsProps {
  element: UserGridRow;
  showingDeleted: boolean;
  onEdit: (row: UserGridRow) => void;
  onDelete: (row: UserGridRow) => void;
  onRestore: (row: UserGridRow) => void;
}

/** The pill colour of a user's status (Active, Inactive or Deleted). */
const userStatusClass = (status: string | null | undefined) =>
  /^inactive/i.test(status ?? '')
    ? 'status-inactive'
    : /^deleted/i.test(status ?? '')
      ? 'status-deleted'
      : 'status-active';

function RowActions({ element, showingDeleted, onEdit, onDelete, onRestore }: RowActionsProps) {
  return (
    <div className="d-flex align-items-center grid-actions">
      {!showingDeleted && (
        <Button title="Edit" onClick={() => onEdit(element)}>
          <Icon name="edit" outlined />
        </Button>
      )}
      {!showingDeleted && (
        <Button title="Delete" onClick={() => onDelete(element)}>
          <Icon name="delete_outline" outlined />
        </Button>
      )}
      {showingDeleted && (
        <Button title="Restore" onClick={() => onRestore(element)}>
          <Icon name="restore_from_trash" outlined />
        </Button>
      )}
    </div>
  );
}

/** Angular `UsersListComponent`: Manage > Users grid. */
export function UsersList() {
  const navigate = useNavigate();
  const [totalRecord, setTotalRecord] = useState(0);
  const [dataSource, setDataSource] = useState<UserGridRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** ap5-a3: true while the grid is showing deleted users, which offer Restore instead of Delete. */
  const [showingDeleted, setShowingDeleted] = useState(false);
  // prod-p2: the grid is paged on the server; only one page of rows is ever fetched.
  const [pageIndex, setPageIndexState] = useState(0);
  const [pageSize, setPageSizeState] = useState(25);
  const pageIndexRef = useRef(0);
  const pageSizeRef = useRef(25);
  const mounted = useRef(false);
  // prod-p2: user ID ascending is the deterministic default order, matching the client grids.
  const queryRef = useRef<UserGridQuery>({
    pageNumber: 1,
    pageSize: 25,
    sortBy: 'userId',
    sortOrder: true,
    searchText: '',
    // the list opens on the Active users (the header's Status filter shows Active)
    statusType: 1,
    recordStatus: 0,
  });

  const setPage = (nextIndex: number, nextSize: number) => {
    pageIndexRef.current = nextIndex;
    pageSizeRef.current = nextSize;
    setPageIndexState(nextIndex);
    setPageSizeState(nextSize);
  };

  function getList(): void {
    practiceService.getUserGrid(queryRef.current).then(
      (response) => {
        if (!mounted.current) return;
        setDataSource(response.userList);
        setTotalRecord(response.pagination.totalItemCount);
        setSelectedIds(new Set());

        // prod-p2: deleting the last rows of the last page would otherwise leave an empty grid.
        if (response.userList.length === 0 && pageIndexRef.current > 0) {
          goToPage(pageIndexRef.current - 1, pageSizeRef.current);
        }
      },
      (error) => notifyError(error),
    );
  }

  /** prod-p2: moves the server-side page; the filters and the sort order are kept. */
  function goToPage(nextIndex: number, nextSize: number): void {
    setPage(nextIndex, nextSize);
    queryRef.current = { ...queryRef.current, pageNumber: nextIndex + 1, pageSize: nextSize };
    getList();
  }

  useEffect(() => {
    mounted.current = true;
    getList();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getList only reads refs and stable setters
  }, []);

  const onFilter = (filter: UserFilter) => {
    setPage(0, pageSizeRef.current);
    queryRef.current = { ...queryRef.current, ...filter, pageNumber: 1, pageSize: pageSizeRef.current };
    setShowingDeleted(queryRef.current.recordStatus === 1);
    getList();
  };

  const onExport = (format: ExportFormatCode) => {
    const query = queryRef.current;
    practiceService
      .exportUsers({ format, ids: [...selectedIds], searchText: query.searchText, statusType: query.statusType })
      .catch((error) => notifyError(error));
  };

  const onEditClick = (row: UserGridRow) => {
    void navigate(matrixPath('/home/manage', 'addUser', { id: btoa(row.universalId) }));
  };

  const isAllSelected = dataSource.length > 0 && dataSource.every((row) => selectedIds.has(row.universalId));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(new Set(checked ? dataSource.map((row) => row.universalId) : []));
  };

  const toggleRow = (row: UserGridRow, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.universalId);
      } else {
        next.delete(row.universalId);
      }
      return next;
    });
  };

  const idsFor = (row: UserGridRow): string[] =>
    selectedIds.has(row.universalId) && selectedIds.size > 1 ? [...selectedIds] : [row.universalId];

  const restore = (ids: string[]) => {
    practiceService.restoreUsers(ids).then(
      () => {
        notifySuccess(ids.length > 1 ? 'Users restored successfully.' : 'User restored successfully.');
        getList();
      },
      (error) => notifyError(error),
    );
  };

  const confirmDelete = (ids: string[]) => {
    const dialogRef = openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 ? 'Users' : 'User', count: ids.length },
    });

    void dialogRef.afterClosed().then((confirmed) => {
      if (!confirmed) {
        return;
      }
      practiceService.deleteUsers(ids).then(
        () => {
          notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
          getList();
        },
        (error) => notifyError(error),
      );
    });
  };

  const columns: DataTableColumn<UserGridRow>[] = [
    {
      id: 'id',
      header: (
        <>
          <Checkbox checked={isAllSelected} onChange={(event) => toggleAll(event.target.checked)} />
          ID
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={selectedIds.has(element.universalId)}
            onChange={(event) => toggleRow(element, event.target.checked)}
          />
          <a className="link" {...buttonLinkProps(() => onEditClick(element))}>
            {element.id}
          </a>
        </>
      ),
    },
    {
      id: 'username',
      header: 'User Name',
      cell: (element) => (
        <span className="name-cell">
          <Avatar name={element.userName} />
          {element.userName}
        </span>
      ),
    },
    { id: 'email', header: 'Email', cell: (element) => element.email },
    { id: 'createdon', header: 'Created on', cell: (element) => element.createdOn },
    { id: 'usertype', header: 'User Type', cell: (element) => element.userType },
    {
      id: 'status',
      header: 'Status',
      cell: (element) => <span className={`status-chip ${userStatusClass(element.status)}`}>{element.status}</span>,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <RowActions
          element={element}
          showingDeleted={showingDeleted}
          onEdit={onEditClick}
          onDelete={(row) => confirmDelete(idsFor(row))}
          onRestore={(row) => restore(idsFor(row))}
        />
      ),
    },
  ];

  return (
    <div className="content client-list manage-users-list">
      <div className="content-body">
        <UsersHeader onFilterChanged={onFilter} onExportRequested={onExport} />

        {/* ap5-a3: selecting rows now offers the actions that apply to a selection. */}
        {selectedIds.size > 0 && (
          <div className="d-flex align-items-center gap-15 mb-1">
            <span>{selectedIds.size} selected</span>
            {!showingDeleted && (
              <Button
                className="action-button danger-button"
                onClick={() => selectedIds.size > 0 && confirmDelete([...selectedIds])}
              >
                <Icon name="delete_outline" outlined />
                <span>Delete selected</span>
              </Button>
            )}
            {showingDeleted && (
              <Button
                className="action-button secondary-button"
                onClick={() => selectedIds.size > 0 && restore([...selectedIds])}
              >
                <Icon name="restore_from_trash" outlined />
                <span>Restore selected</span>
              </Button>
            )}
            <Button className="action-button secondary-button" onClick={() => onExport(ExportFormatCode.Excel)}>
              <Icon name="border_all" className="icon-color-xls" />
              <span>Export selected</span>
            </Button>
          </div>
        )}

        <div className="mat-table-wrapper mat-table-wrapper-pagination">
          <DataTable columns={columns} rows={dataSource} rowKey={(row) => row.universalId} stickyHeader />
        </div>

        {dataSource.length === 0 && <div className="text-align-center p-1">No users match these filters.</div>}

        {/* prod-p2: the grid is paged on the server. */}
        {totalRecord > 0 && (
          <Paginator
            length={totalRecord}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageSizeOptions={pageSizeOptions}
            showFirstLastButtons
            onPage={(event) => goToPage(event.pageIndex, event.pageSize)}
          />
        )}
      </div>
    </div>
  );
}
